import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import { useComponentTheme } from '@chegg-ui/theme';
import { getThemeConfig } from '@chegg-ui/input';
import { InputWrapper } from '@chegg-ui/input-wrapper';
import { Listbox } from '@chegg-ui/listbox';
import { useAutocompleteBehavior } from '../externals';
import { FauxInputCombobox, AutocompleteInput, AriaLive } from '../styles';
// TODO: the a11y behavior of this component is bit weird, it's _mostly_ identical to example 1 at:
// https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
// the only difference is we show any available options right away, while the example waits for input
var Autocomplete = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var dataTest = _ref['data-test'],
      adjustToBackgroundColor = _ref.adjustToBackgroundColor,
      className = _ref.className,
      disabled = _ref.disabled,
      errorCta = _ref.errorCta,
      errorText = _ref.errorText,
      helperText = _ref.helperText,
      inputId = _ref.inputId,
      labelText = _ref.labelText,
      leadingIcon = _ref.leadingIcon,
      leadingIconClickable = _ref.leadingIconClickable,
      _ref$maxListboxHeight = _ref.maxListboxHeight,
      maxListboxHeight = _ref$maxListboxHeight === void 0 ? '200px' : _ref$maxListboxHeight,
      onInputBlur = _ref.onInputBlur,
      onInputChange = _ref.onInputChange,
      onInputFocus = _ref.onInputFocus,
      onKeydown = _ref.onKeydown,
      onSelect = _ref.onSelect,
      onSubmit = _ref.onSubmit,
      options = _ref.options,
      theme = _ref.theme,
      trailingIcon = _ref.trailingIcon,
      trailingIconClickable = _ref.trailingIconClickable,
      value = _ref.value,
      visuallyHideLabel = _ref.visuallyHideLabel,
      width = _ref.width,
      remainingSelectProps = _objectWithoutProperties(_ref, ["data-test", "adjustToBackgroundColor", "className", "disabled", "errorCta", "errorText", "helperText", "inputId", "labelText", "leadingIcon", "leadingIconClickable", "maxListboxHeight", "onInputBlur", "onInputChange", "onInputFocus", "onKeydown", "onSelect", "onSubmit", "options", "theme", "trailingIcon", "trailingIconClickable", "value", "visuallyHideLabel", "width"]);

  var _useAutocompleteBehav = useAutocompleteBehavior(ref, Boolean(disabled), value, options, onInputChange, onSelect, onSubmit, onInputBlur, onInputFocus, onKeydown),
      inputRef = _useAutocompleteBehav.inputRef,
      isComboboxFocused = _useAutocompleteBehav.isComboboxFocused,
      isLabelHovered = _useAutocompleteBehav.isLabelHovered,
      isListboxOpen = _useAutocompleteBehav.isListboxOpen,
      onAutocompleteInputBlur = _useAutocompleteBehav.onAutocompleteInputBlur,
      onAutocompleteInputChange = _useAutocompleteBehav.onAutocompleteInputChange,
      onAutocompleteInputFocus = _useAutocompleteBehav.onAutocompleteInputFocus,
      onAutocompleteInputKeyDown = _useAutocompleteBehav.onAutocompleteInputKeyDown,
      onLabelMouseEnter = _useAutocompleteBehav.onLabelMouseEnter,
      onLabelMouseLeave = _useAutocompleteBehav.onLabelMouseLeave,
      onSelectListboxOption = _useAutocompleteBehav.onSelectListboxOption,
      pseudoFocusedListboxIndex = _useAutocompleteBehav.pseudoFocusedListboxIndex,
      selectedListboxIndex = _useAutocompleteBehav.selectedListboxIndex;

  var inputComponentTheme = useComponentTheme({
    name: 'Input',
    theme: getThemeConfig(theme === null || theme === void 0 ? void 0 : theme.input)
  });
  return /*#__PURE__*/React.createElement(InputWrapper, {
    adjustToBackgroundColor: adjustToBackgroundColor,
    className: className,
    "data-test": dataTest,
    disabled: disabled,
    errorCta: errorCta,
    errorText: errorText,
    helperText: helperText,
    inputId: inputId,
    labelText: labelText,
    leadingIcon: leadingIcon,
    leadingIconClickable: leadingIconClickable,
    onLabelMouseEnter: onLabelMouseEnter,
    onLabelMouseLeave: onLabelMouseLeave,
    trailingIcon: trailingIcon,
    trailingIconClickable: trailingIconClickable,
    visuallyHideLabel: visuallyHideLabel,
    width: width
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FauxInputCombobox, {
    "aria-expanded": isListboxOpen,
    "aria-haspopup": "listbox",
    "aria-owns": "".concat(inputId, "_listbox"),
    "data-test": "".concat(dataTest || inputId, "_combobox"),
    id: "".concat(inputId, "_combobox"),
    role: "combobox"
  }, /*#__PURE__*/React.createElement(AutocompleteInput, _extends({
    adjustToBackgroundColor: adjustToBackgroundColor,
    customTheme: inputComponentTheme,
    isFocused: isComboboxFocused,
    isLabelHovered: isLabelHovered,
    disabled: disabled,
    errorText: errorText,
    hasLeadingIcon: Boolean(leadingIcon),
    hasTrailingIcon: Boolean(trailingIcon),
    "aria-activedescendant": ~pseudoFocusedListboxIndex ? "".concat(inputId, "_option_").concat(pseudoFocusedListboxIndex) : undefined,
    "aria-autocomplete": "list",
    "aria-controls": "".concat(inputId, "_listbox"),
    autoCapitalize: "off",
    autoComplete: "off",
    autoCorrect: "off",
    "data-test": dataTest || inputId,
    id: inputId,
    onBlur: onAutocompleteInputBlur,
    onChange: onAutocompleteInputChange,
    onFocus: onAutocompleteInputFocus,
    onKeyDown: onAutocompleteInputKeyDown,
    ref: inputRef,
    spellCheck: "false",
    type: "text",
    value: value
  }, remainingSelectProps))), /*#__PURE__*/React.createElement(Listbox, {
    inputId: inputId,
    isOpen: isListboxOpen,
    maxHeight: maxListboxHeight,
    onSelectOption: onSelectListboxOption,
    options: options,
    pseudoFocusedIndex: pseudoFocusedListboxIndex,
    selectedIndex: selectedListboxIndex
  }), /*#__PURE__*/React.createElement(AriaLive, {
    "aria-live": "polite",
    role: "status"
  }, options.length ? "".concat(options.length, " results available.") : 'No results.')));
});
Autocomplete.displayName = 'Autocomplete';
export default Autocomplete;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg-ui/autocomplete/dist/esm/variants/Autocomplete.js
// module id = 4505
// module chunks = 11